import { useState, useRef } from 'react';

export default function HeroSection({ onOpenModal }: { onOpenModal: () => void }) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const handleToggle = () => {
    if (playing) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  return (
    <section className="w-full min-h-screen flex flex-col lg:flex-row bg-background-400 overflow-hidden pt-14 relative">
      {/* Paw print decorations */}
      <div className="absolute inset-0 pointer-events-none select-none z-0" aria-hidden="true">
        <div className="w-14 h-14 flex items-center justify-center absolute top-[18%] left-[55%] opacity-[0.05] rotate-[-20deg] animate-float" style={{ animationDelay: '0s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute top-[30%] right-[12%] opacity-[0.04] rotate-[35deg] animate-drift" style={{ animationDelay: '0.8s' }}>
          <i className="fa-solid fa-paw text-4xl text-foreground-950"></i>
        </div>
        <div className="w-16 h-16 flex items-center justify-center absolute top-[50%] left-[58%] opacity-[0.06] rotate-[15deg] animate-float" style={{ animationDelay: '1.6s' }}>
          <i className="fa-solid fa-paw text-6xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute top-[65%] right-[8%] opacity-[0.05] rotate-[-45deg] animate-drift" style={{ animationDelay: '2.4s' }}>
          <i className="fa-solid fa-paw text-3xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute top-[78%] left-[52%] opacity-[0.04] rotate-[25deg] animate-float" style={{ animationDelay: '3.2s' }}>
          <i className="fa-solid fa-paw text-4xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute top-[88%] right-[20%] opacity-[0.05] rotate-[-10deg] animate-drift" style={{ animationDelay: '4s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-10 h-10 flex items-center justify-center absolute top-[10%] right-[25%] opacity-[0.04] rotate-[50deg] animate-float" style={{ animationDelay: '5s' }}>
          <i className="fa-solid fa-paw text-3xl text-foreground-950"></i>
        </div>
        <div className="w-16 h-16 flex items-center justify-center absolute top-[40%] right-[28%] opacity-[0.03] rotate-[-30deg] animate-drift" style={{ animationDelay: '3.6s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
      </div>

      <div className="w-full lg:w-1/2 relative min-h-[300px] lg:min-h-screen z-10">
        <video
          src="https://cdn.gamma.app/z4s3rpnzx64fspv/edited-images/IFyjR2GJuhQjlO_kXScQy.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 py-10 lg:py-16 gap-5 relative z-10">
        <div className="flex justify-center mb-1">
          <img
            alt="Gracie & Lily Logo"
            className="h-40 md:h-60 w-auto object-contain drop-shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
            src="https://public.readdy.ai/ai/img_res/91ea3fda-8705-4921-ab9c-220fa8418490.png"
          />
        </div>

        <div className="w-full rounded-2xl overflow-hidden mb-2 relative cursor-pointer group" onClick={handleToggle}>
          <video
            ref={videoRef}
            src="https://storage.readdy-site.link/project_files/61f6dd57-4435-4fb1-9f7a-943f2831e007/0b863db0-7de0-4122-9e1f-e6e439a5741a_Gracie--Lily-welcome-video.mp4?v=deaaf4b416b2514cb799cf062afe3d6e"
            playsInline
            muted
            className="w-full h-auto block"
          />
          {!playing ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); handlePlay(); }}
                className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 cursor-pointer"
                aria-label="Play video"
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <i className="ri-play-fill text-primary-500 text-3xl"></i>
                </div>
              </button>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); handlePause(); }}
                className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 cursor-pointer"
                aria-label="Pause video"
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <i className="ri-pause-fill text-primary-500 text-3xl"></i>
                </div>
              </button>
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleMute(); }}
            className="absolute bottom-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white hover:scale-110 transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100 z-10"
            aria-label={muted ? 'Unmute video' : 'Mute video'}
          >
            <i className={`${muted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-primary-500 text-base`}></i>
          </button>
        </div>

        <p className="text-secondary-500 font-semibold text-sm">✨ 13 Magical Adventures Await!</p>

        <h1 className="text-4xl md:text-5xl font-black leading-tight font-heading text-foreground-950">
          Meet Gracie & Lily
        </h1>

        <h2 className="text-xl md:text-2xl font-extrabold text-primary-500 leading-snug -mt-2">
          The Real-Life Bichon Sisters Behind Magical Children's Adventures!
        </h2>

        <p className="text-foreground-700 text-lg leading-relaxed">
          📚 Beautiful illustrated books that bring joy, bravery, and magic to children everywhere. Available in books, flipbooks, and adorable merchandise!
        </p>

        <div className="flex flex-col gap-2 w-full max-w-xs mx-auto lg:mx-0">
          <button
            onClick={() => scrollToSection('books')}
            className="bg-secondary-500 text-background-50 px-4 py-2 rounded-lg font-bold text-sm text-center whitespace-nowrap cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-200"
            title="Jump to our book collection"
          >
            Ages 3-8
          </button>
          <button
            onClick={() => scrollToSection('stories')}
            className="bg-secondary-400 text-background-50 px-4 py-2 rounded-lg font-bold text-sm text-center whitespace-nowrap cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-200"
            title="Jump to bedtime stories"
          >
            Perfect for Bedtime
          </button>
          <button
            onClick={() => scrollToSection('books')}
            className="bg-primary-500 text-background-50 px-4 py-2 rounded-lg font-bold text-sm text-center whitespace-nowrap cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-200"
            title="Jump to our illustrated books"
          >
            Beautiful Illustrations
          </button>
          <button
            onClick={() => scrollToSection('sisters')}
            className="bg-primary-100 text-primary-700 px-4 py-2 rounded-lg font-bold text-sm text-center whitespace-nowrap cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-200"
            title="Jump to meet the sisters"
          >
            Real Dogs, Real Adventures
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-1">
          <button
            onClick={() => {
              const el = document.getElementById('books');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors text-center"
          >
            Shop Books
          </button>
          <a
            href="https://gracie-and-lilys-adventures.myshopify.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-primary-500 text-primary-500 px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-500 hover:text-white transition-colors text-center"
          >
            Shop Merchandise
          </a>
          <a
            href="https://a.co/d/0gs915Vo"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-accent-500 text-accent-500 px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-500 hover:text-white transition-colors text-center flex items-center justify-center gap-2"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-amazon-fill text-base"></i>
            </span>
            Shop on Amazon
          </a>
        </div>

        <p
          onClick={onOpenModal}
          className="text-secondary-500 text-xs mt-1 cursor-pointer hover:text-primary-500 hover:underline transition-colors"
        >
          Get a FREE coloring book when you join our adventure! Claim yours now →
        </p>
      </div>
    </section>
  );
}